import { IButtonOptions } from "devextreme-react/button";
import { List } from "devextreme-react/list";
import { LoadPanel } from "devextreme-react/load-panel";
import { Popup, ToolbarItem } from "devextreme-react/popup";
import { CustomStore, DataSource } from "devextreme/common/data";
import notify from "../../lib/notify";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  assignEmployeesToProject,
  getProjectEmployeesAvailableToAssign,
} from "../../api/projects";
import { CEID, Project } from "../../types";
import { useScreenSize } from "../../utils/media-query";

type ProjectSupervisorAssignListPopupProps = {
  project: Project;
  onClose: (refresh: boolean) => void;
};

export default function ProjectSupervisorAssignListPopup({
  project,
  onClose,
}: ProjectSupervisorAssignListPopupProps) {
  const { t } = useTranslation();
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;

  const [isLoading, setIsLoading] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState<CEID[]>([]);

  const dataSource = useMemo(() => {
    return new DataSource({
      store: new CustomStore({
        loadMode: "raw",
        key: ["companyId", "employeeId"],
        load: async () =>
          await getProjectEmployeesAvailableToAssign(project.id),
      }),
    });
  }, [project.id]);

  const handleAssignButtonClick = useCallback(async () => {
    setIsLoading(true);

    try {
      await assignEmployeesToProject(project.id, selectedEmployees);

      notify(
        t(
          "projectSupervisorAssignList.selectedManagersHaveBeenAssignedToProject",
        ),
        "success",
        5000,
      );

      onClose(true);
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : t("common.unknownErrorOccurred"),
        "error",
        5000,
      );
    } finally {
      setIsLoading(false);
    }
  }, [project.id, selectedEmployees, onClose, t]);

  return (
    <>
      {isLoading ? (
        <LoadPanel
          shading
          shadingColor="rgba(0, 0, 0, 0.3)"
          visible
          message={t("common.processingRequest")}
        />
      ) : null}
      <Popup
        wrapperAttr={{ class: "projects-popup" }}
        visible
        title={t("projectSupervisorAssignList.title", {
          projectAcronym: project.acronym,
        })}
        onHiding={() => onClose(false)}
        fullScreen={isMobile}
        maxWidth={isMobile ? undefined : 500}
        height={isMobile ? "100%" : "auto"}
        maxHeight={isMobile ? undefined : "80vh"}
        showCloseButton
        dragEnabled={!isMobile}
      >
        <List
          dataSource={dataSource}
          displayExpr="fullName"
          searchEnabled
          searchExpr="fullName"
          showSelectionControls
          selectionMode="all"
          selectByClick
          onSelectionChanged={(event) => {
            setSelectedEmployees(
              event.component.option("selectedItemKeys") as CEID[],
            );
          }}
          height={isMobile ? "calc(100vh - 200px)" : 300}
        />
        <ToolbarItem
          toolbar="bottom"
          location="after"
          widget="dxButton"
          options={
            {
              type: "default",
              stylingMode: "contained",
              text: t("projectSupervisorAssignList.assignToProject"),
              onClick: handleAssignButtonClick,
              disabled: selectedEmployees.length === 0,
            } as IButtonOptions
          }
        />
      </Popup>
    </>
  );
}
